import { FC, useCallback, useState } from 'react';
import { Button, CustomModal, CustomTextArea } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CircleCloseIcon } from 'assets/icons';

const FEEDBACK_OPTIONS: { id: string; label: string }[] = [
  { id: 'line_items_off', label: 'Line items were off' },
  { id: 'totals_tax_off', label: 'Totals/tax were off' },
  { id: 'missing_something', label: 'Missing something' },
  { id: 'wrong_customer', label: 'Wrong customer' },
  { id: 'other', label: 'Other' }
];

interface AutoFillFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendFeedback?: (reason: string, comment: string) => Promise<void>;
  isLoading?: boolean;
  options?: { id: string; label: string }[];
}

const AutoFillFeedbackModal: FC<AutoFillFeedbackModalProps> = ({
  isOpen,
  onClose,
  onSendFeedback,
  isLoading = false,
  options = FEEDBACK_OPTIONS
}) => {
  const [selectedReason, setSelectedReason] = useState<{ id: string; label: string } | null>(null);
  const [comment, setComment] = useState('');

  const handleClose = useCallback(() => {
    setSelectedReason(null);
    setComment('');
    onClose();
  }, [onClose]);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleReasonClick = useCallback((option: { id: string; label: string }) => {
    setSelectedReason(option);
  }, []);

  const handleSendFeedback = useCallback(async () => {
    if (selectedReason && onSendFeedback) {
      await onSendFeedback(selectedReason.label, comment.trim());
      handleClose();
    }
  }, [selectedReason, comment, onSendFeedback, handleClose]);

  const isSendDisabled = !selectedReason || (selectedReason.id === 'other' && !comment.trim());

  return (
    <CustomModal
      open={isOpen}
      closeModal={handleClose}
      width="md:w-[550px]"
      height="md:min-h-[253px] md:max-h-[90vh]"
      enableOutsideClick={false}
      className="max-h-[90vh] overflow-hidden">
      <div className="relative flex h-full flex-col">
        <CircleCloseIcon
          className="absolute top-2 right-2 z-[1] shrink-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Close feedback modal"
        />
        <div className="shrink-0 p-6">
          <h2 className="text-xl font-bold text-primaryText">What went wrong?</h2>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          <div className="mb-4 flex flex-wrap gap-4">
            {options?.map(option => (
              <button
                key={option.id}
                type="button"
                disabled={isLoading}
                onClick={() => handleReasonClick(option)}
                tabIndex={0}
                aria-pressed={selectedReason?.id === option.id}
                aria-label={option.label}
                className={`rounded border p-2.5 text-sbase font-semibold text-primaryText transition-colors 
                  ${isLoading ? 'cursor-not-allowed opacity-50' : ''} ${
                  selectedReason?.id === option.id
                    ? 'border-secondary bg-secondaryBtn '
                    : 'border-border hover:border-secondary'
                }`}>
                {option.label}
              </button>
            ))}
          </div>

          {selectedReason?.id === 'other' && (
            <div className="mb-4">
              <CustomTextArea
                id="autofill-feedback-comment"
                name="autofill-feedback-comment"
                placeholder="What should we change?"
                value={comment}
                onChange={handleCommentChange}
                className="!min-h-[100px] border border-borderGray outline-none"
                maxLength={1000}
              />
            </div>
          )}
        </div>

        <div className="shrink-0 px-6 pb-6 pt-2">
          <Button
            onClick={handleSendFeedback}
            type={ButtonType.PRIMARY}
            disabled={isSendDisabled}
            isLoading={isLoading}
            className="h-10 w-full"
            id="send-autofill-feedback-button">
            Send feedback
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default AutoFillFeedbackModal;
